import { addDays, format, parseISO } from 'date-fns'
import type { BulkAssignShiftResult, ShiftEligibilityReason } from '@/types/shift.types'

/** Error code the API returns (409) when a person may not work a shift. */
export const EMPLOYEE_NOT_ELIGIBLE = 'EMPLOYEE_NOT_ELIGIBLE'

function formatDay(ymd: string, offsetDays = 0): string {
  const date = parseISO(ymd)
  return Number.isNaN(date.getTime()) ? ymd : format(addDays(date, offsetDays), 'd MMM yyyy')
}

/** One reason as a lower-case clause, e.g. "they are on approved leave". */
export function describeEligibilityReason(reason: ShiftEligibilityReason): string {
  switch (reason.code) {
    case 'ACCOUNT_NOT_ACTIVE':
      return `their account is ${reason.accountAccess ?? 'not active'}`
    case 'EMPLOYMENT_ENDED':
      // effectiveFrom is the first day they may not work; the day before is
      // their last working day, which they still work.
      return reason.effectiveFrom
        ? `their last working day is ${formatDay(reason.effectiveFrom, -1)}`
        : 'their employment has ended'
    case 'EMPLOYMENT_SUSPENDED':
      return 'their employment is suspended'
    case 'ON_APPROVED_LEAVE':
      return 'they are on approved leave'
    default:
      return reason.message
  }
}

export function describeEligibilityReasons(reasons: ShiftEligibilityReason[]): string {
  const parts = reasons.map(describeEligibilityReason)
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/** Tooltip/badge copy for a scheduled shift whose person became ineligible. */
export function eligibilityWarningText(reasons: ShiftEligibilityReason[]): string {
  const text = describeEligibilityReasons(reasons)
  return `Can't work this shift: ${text}. Reassign or cancel it.`
}

/**
 * Copy for a single assign/reassign refused with EMPLOYEE_NOT_ELIGIBLE, or
 * null when the error is something else.
 */
export function getShiftEligibilityErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { code?: string; date?: string; reasons?: ShiftEligibilityReason[] }
    | undefined
  if (data?.code !== EMPLOYEE_NOT_ELIGIBLE) return null
  const reasons = Array.isArray(data.reasons) ? data.reasons : []
  const on = data.date ? ` on ${formatDay(data.date)}` : ''
  const why = describeEligibilityReasons(reasons)
  return `Can't schedule this employee${on}${why ? `: ${why}` : ''}.`
}

/**
 * One line per skipped person for a bulk result, e.g.
 * "Skipped: Ali Khan (2 days, they are on approved leave)." Empty when no
 * one was refused by the eligibility rule.
 */
export function summarizeIneligibleBulkErrors(
  errors: BulkAssignShiftResult['errors'],
  userNames: Record<string, string> = {}
): string {
  const byUser = new Map<string, { days: number; reasons: Map<string, ShiftEligibilityReason> }>()
  for (const error of errors) {
    if (error.code !== EMPLOYEE_NOT_ELIGIBLE) continue
    const entry = byUser.get(error.userId) ?? { days: 0, reasons: new Map() }
    entry.days += 1
    for (const reason of error.reasons ?? []) {
      entry.reasons.set(describeEligibilityReason(reason), reason)
    }
    byUser.set(error.userId, entry)
  }
  if (byUser.size === 0) return ''
  const parts = [...byUser.entries()].map(([userId, entry]) => {
    const who = userNames[userId] ?? '1 employee'
    const days = `${entry.days} day${entry.days === 1 ? '' : 's'}`
    return `${who} (${days}, ${describeEligibilityReasons([...entry.reasons.values()])})`
  })
  return `Skipped: ${parts.join('; ')}.`
}
